/**
 * WHAT A BRANCH DRAWS: the Simple and Full editions.
 *
 * Ported from the POC (`core/lib/edition.ts`). A screen never learns the
 * edition exists — it asks for a FEATURE (`useFeature("cycleCounts")`), so this
 * table is the one place a reader goes to find out what Simple drops.
 *
 * `IN_SIMPLE` is a Record rather than a set of what is dropped: a feature added
 * tomorrow will not compile until somebody decides whether Simple has it.
 *
 * The rules that come with it:
 * - `full` is the default. Absent, rubbish and an unknown branch are all full,
 *   because a screen must never disappear by accident.
 * - Simple is only offered where the branch runs the retail module alone. The
 *   server derives the edition in force; the client reads what it is given.
 * - The edition hides OFFERS and never COUNTS: it takes away the door to making
 *   something new, never a record that already exists.
 */

export type Edition = "full" | "simple";

export type Feature =
  /** Appointments and the waiting room. */
  | "booking"
  /** Work that produces no medicine: services, signed clinical notes. */
  | "clinicalServices"
  /** Asking a prescriber a question. */
  | "clarifications"
  /** The near-miss and incident register. */
  | "incidents"
  /** Linking two patient records as one person or one household. */
  | "patientLinks"
  /** Bagging a supply and parking it on the holding rack. */
  | "medicationBags"
  /** Timing a refrigerated line out of the fridge. */
  | "coldChainClock"
  /** A fenced supply expiring when nobody collects it. */
  | "holdExpiry"
  /** Recording a supply at an earlier date. */
  | "backdating"
  /** The Dangerous Drugs, psychotropic, antibiotic and poisons books. */
  | "controlledRegisters"
  /** Drawing a signature to post the day-end. */
  | "dayEndSignature"
  /** Named shelf locations. */
  | "shelves"
  /** The nearly-expired filter on the shelf. */
  | "expiringSoon"
  /** Days-of-cover bands. */
  | "coverBands"
  /** Condemning stock to the quarantine bin. */
  | "quarantine"
  /** Returns to a supplier. */
  | "returns"
  /** Cycle counts. */
  | "cycleCounts"
  /** Requisitions split into purchase orders. */
  | "requisitions"
  /** Selling by the pack as well as the basic unit. */
  | "packSelling"
  /** Quantity breaks and free goods at the till. */
  | "quantityOffers"
  /** Enrolment columns across the estate. */
  | "multiBranch"
  /** The overview report. */
  | "overviewReport"
  /** The retail report. */
  | "retailReport"
  /** The shelf report. */
  | "shelfReport";

export const IN_SIMPLE: Record<Feature, boolean> = {
  booking: false,
  clinicalServices: false,
  clarifications: false,
  incidents: false,
  patientLinks: false,
  medicationBags: false,
  coldChainClock: false,
  holdExpiry: false,
  backdating: false,
  controlledRegisters: false,
  dayEndSignature: false,
  shelves: false,
  expiringSoon: true,
  coverBands: false,
  quarantine: false,
  returns: false,
  cycleCounts: false,
  requisitions: false,
  packSelling: false,
  quantityOffers: false,
  multiBranch: false,
  overviewReport: false,
  retailReport: false,
  shelfReport: false,
};

export const FEATURES = Object.keys(IN_SIMPLE) as Feature[];

/** Whitelisted, never cast: anything that is not exactly "simple" is full. */
export function readEdition(value: unknown): Edition {
  return value === "simple" ? "simple" : "full";
}

/** Is this feature on at a branch in this edition? */
export function featureOn(edition: Edition, feature: Feature): boolean {
  return edition === "full" || IN_SIMPLE[feature];
}
